import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getMetadata, type Metadata } from "../api/client";
import type { ServiceSlug } from "../api/client";
import type { TimeRange } from "../utils/time";

export interface UseMetadataOptions {
  timeRange?: TimeRange | null;
}

export interface UseMetadataResult {
  metadata: Metadata | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for fetching metadata (services and metrics).
 *
 * @param serviceSlug - The OpenMon service slug (scopes all queries)
 * @param options - Optional configuration including time range filter
 * @returns Object containing metadata, loading state, and error
 */
export function useMetadata(
  serviceSlug: ServiceSlug | null,
  options?: UseMetadataOptions,
): UseMetadataResult {
  const query = useQuery({
    queryKey: ["metadata", serviceSlug, options?.timeRange?.from, options?.timeRange?.to],
    enabled: !!serviceSlug,
    queryFn: ({ signal }) =>
      getMetadata(serviceSlug!, {
        rangeFrom: options?.timeRange?.from,
        rangeTo: options?.timeRange?.to,
        signal,
      }),
  });

  if (!serviceSlug) {
    return { metadata: null, loading: false, error: null };
  }

  const metadata = query.data
    ? ({
        metrics: query.data.metrics ?? [],
        services: query.data.services ?? [],
      } as Metadata)
    : null;

  const error =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? "Failed to load metadata"
        : null;

  return { metadata, loading: query.isLoading, error };
}
